import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Trophy, Gift, Calendar, AlertCircle, CheckCircle2, Loader2, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface Competition {
  id: number;
  title: string;
  prizeAmountPence: number;
  entryFeePence: number;
  startDate: string;
  endDate: string;
  drawDate: string;
  enabled: boolean;
  status: string;
  winnerName: string | null;
  termsUrl: string | null;
  entryCount: number;
}

function pnd(p: number) { return `£${(p / 100).toFixed(0)}`; }
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function Competition() {
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [mobile, setMobile] = useState("");
  const [ayrshireConfirmed, setAyrshireConfirmed] = useState(false);
  const [idConfirmed, setIdConfirmed] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/competitions/current")
      .then(r => r.json() as Promise<{ competition: Competition | null }>)
      .then(d => setCompetition(d.competition))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError("Please enter your full name."); return; }
    if (!email.trim() || !email.includes("@")) { setError("Please enter a valid email address."); return; }
    if (!address.trim()) { setError("Please enter your full address."); return; }
    if (!mobile.trim()) { setError("Please enter your mobile number."); return; }
    if (!ayrshireConfirmed) { setError("You must confirm you are an Ayrshire resident to enter."); return; }
    if (!idConfirmed) { setError("You must confirm you can provide ID if required."); return; }
    if (!termsAgreed) { setError("Please agree to the terms and conditions."); return; }
    setSubmitting(true);
    try {
      const r = await fetch("/api/competitions/enter-free", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          address: address.trim(),
          mobile: mobile.trim(),
        }),
      });
      const d = await r.json() as { message?: string; error?: string };
      if (!r.ok) { setError(d.error ?? "Something went wrong. Please try again."); return; }
      setSubmitted(true);
    } catch { setError("Network error. Please try again."); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="container mx-auto px-4 py-10 max-w-2xl">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : !competition ? (
          <div className="text-center py-20">
            <Trophy className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-800 mb-2">No Active Competition</h1>
            <p className="text-slate-500">There's no competition running right now. Check back soon — we run weekly cash draws!</p>
          </div>
        ) : competition.status === "drawn" ? (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-10 h-10 text-amber-500" />
              </div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2">{competition.title}</h1>
              <p className="text-slate-500">This competition has ended</p>
            </div>
            {competition.winnerName && (
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-2xl p-8 text-center">
                <p className="text-sm text-amber-600 font-semibold uppercase tracking-wide mb-2">🎉 Winner</p>
                <p className="text-3xl font-bold text-amber-700">{competition.winnerName}</p>
                <p className="text-amber-600 mt-2">Congratulations on winning {pnd(competition.prizeAmountPence)}!</p>
              </div>
            )}
            <p className="text-center text-slate-500 text-sm">
              Watch out for our next competition — follow us on social media or <Link href="/signup" className="text-primary underline">join the Hub</Link> to get notified!
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Hero */}
            <div className="bg-gradient-to-br from-primary to-blue-800 rounded-2xl p-8 text-white text-center">
              <Trophy className="w-12 h-12 text-amber-400 mx-auto mb-3" />
              <h1 className="text-3xl font-bold mb-1">{competition.title}</h1>
              <p className="text-white/70 mb-4">Win big — absolutely free to enter!</p>
              <div className="text-6xl font-black text-amber-400 mb-2">{pnd(competition.prizeAmountPence)}</div>
              <p className="text-white/80 text-sm">Cash prize drawn on {fmtDate(competition.drawDate)}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                <Gift className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-800">{pnd(competition.prizeAmountPence)}</p>
                <p className="text-xs text-slate-500">Prize</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                <Trophy className="w-5 h-5 text-green-500 mx-auto mb-1" />
                <p className="text-xl font-bold text-slate-800">{competition.entryCount}</p>
                <p className="text-xs text-slate-500">Entries</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                <Calendar className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <p className="text-xs font-bold text-slate-800">{new Date(competition.drawDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
                <p className="text-xs text-slate-500">Draw Date</p>
              </div>
            </div>

            {/* Free entry form */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-lg font-bold text-slate-800 mb-1">Free Entry</h2>
              <p className="text-slate-500 text-sm mb-5">
                No purchase needed. One free entry per person per week. Ayrshire residents only.
                Winners must provide photo ID and proof of Ayrshire address to claim the prize.
              </p>

              {submitted ? (
                <div className="flex flex-col items-center py-6 text-center gap-3">
                  <CheckCircle2 className="w-12 h-12 text-green-500" />
                  <p className="text-lg font-bold text-slate-800">You're entered! Good luck! 🎉</p>
                  <p className="text-slate-500 text-sm">We'll announce the winner on {fmtDate(competition.drawDate)}.</p>
                </div>
              ) : (
                <form onSubmit={e => void handleSubmit(e)} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. John Smith" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
                    <p className="text-xs text-slate-400 mt-1">We'll only use this to contact you if you win.</p>
                  </div>
                  <div>
                    <Label htmlFor="mobile" className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" /> Mobile Number
                    </Label>
                    <Input id="mobile" type="tel" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="e.g. 07700 900123" />
                  </div>
                  <div>
                    <Label htmlFor="address" className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" /> Full Ayrshire Address
                    </Label>
                    <Textarea
                      id="address"
                      value={address}
                      onChange={e => setAddress(e.target.value)}
                      placeholder={"e.g. 12 High Street\nAyr\nKA1 1AA"}
                      rows={3}
                    />
                    <p className="text-xs text-slate-400 mt-1">Must be an Ayrshire address. This will be used to verify residency if you win.</p>
                  </div>

                  <div className="space-y-3 pt-1">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={ayrshireConfirmed} onChange={e => setAyrshireConfirmed(e.target.checked)}
                        className="mt-0.5 accent-primary w-4 h-4 flex-shrink-0" />
                      <span className="text-sm text-slate-700 font-medium">
                        I confirm I am a resident of Ayrshire (North, East, or South Ayrshire).
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={idConfirmed} onChange={e => setIdConfirmed(e.target.checked)}
                        className="mt-0.5 accent-primary w-4 h-4 flex-shrink-0" />
                      <span className="text-sm text-slate-700 font-medium">
                        I understand that to claim the prize I may be required to provide valid photo ID and proof of my Ayrshire address.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={termsAgreed} onChange={e => setTermsAgreed(e.target.checked)}
                        className="mt-0.5 accent-primary w-4 h-4 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        I agree to the{" "}
                        {competition.termsUrl
                          ? <a href={competition.termsUrl} target="_blank" rel="noreferrer" className="underline text-primary">competition terms and conditions</a>
                          : "competition terms and conditions"
                        }.{" "}
                        I confirm I am 18+ and an Ayrshire resident.
                      </span>
                    </label>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}
                  <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Entering…</> : "Enter for Free 🎉"}
                  </Button>
                </form>
              )}
            </div>

            {/* How it works */}
            <div className="bg-slate-100 rounded-xl p-5 text-sm text-slate-600 space-y-2">
              <p className="font-semibold text-slate-700">How it works</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Enter free above — one free entry per person per week</li>
                <li>Get a bonus entry automatically when you order through Ayrshire Hub</li>
                <li>Open to Ayrshire residents aged 18+ only</li>
                <li>Winners must provide photo ID and proof of Ayrshire address to claim</li>
                <li>The draw takes place on {fmtDate(competition.drawDate)}</li>
                <li>One winner is selected at random from all entries</li>
                <li>The winner is announced on this page and notified by email</li>
                <li>Prize is {pnd(competition.prizeAmountPence)} cash, paid by bank transfer within 7 days</li>
              </ul>
            </div>
          </div>
        )}
    </div>
  );
}
